// IMP-502: the assessment's scripted math — the missed-revenue number the
// whole flow builds to, and the gaps/reveal rules around it. Everything here
// must stay true for a business we know nothing about — no site-derived
// claims, no invented statistics.
//
// IMP-515: the interview itself (question bank, prompts, chips) moved
// server-side (Discovery::InterviewScript) — the server owns the
// conversation and the client renders whatever it returns. The answer ids
// the math reads are the same ids that script fills.

export type Answers = Record<string, string>

/** The hub's job slugs (PartnerLandingPagesController::JOB_SLUGS) — the
 *  assessment recommends and sells the same trio the page merchandises. */
export type JobSlug = "receptionist" | "website-chat" | "lead-responder"

/** ONE recommended agent (no bundling today). Receptionist is the default
 *  winner whenever phone coverage is bad; the lead responder wins only when
 *  the phone is handled but web follow-up is slow. Chat is never the
 *  primary recommendation — its case rides in the report. */
export function recommendAgent(answers: Answers): JobSlug {
  const phoneCovered = answers.coverage === "service"
  const slowWeb = answers.followup === "day" || answers.followup === "later"
  if (phoneCovered && slowWeb) return "lead-responder"
  return "receptionist"
}

/** Round to a believable figure — $2,847 reads as fake precision. */
export function roundish(value: number) {
  const step = value > 2000 ? 100 : 50
  return Math.round(value / step) * step
}

/** The missed-revenue math behind the reveal. Lost business = missed calls ×
 *  the share that are NEW-BUSINESS inquiries (not existing customers,
 *  suppliers, or junk — this factor is what keeps the number realistic and
 *  makes "in new business" literally true) × the share who never try again
 *  (benchmark) × the share you'd have won. Output is a range, never a point
 *  estimate — and a WIDER range when any input came from a typical-number
 *  stand-in rather than the visitor (IMP-507 round 3: skipped slots widen
 *  the range, they never silently substitute). */
export function missedRevenue(answers: Answers, assumed = false) {
  const { missedPerWeek, winRate, jobValue } = worksheetDefaults(answers)
  const newBusinessShare = 0.4
  const neverRetry = 0.4
  const monthly =
    missedPerWeek *
    4.33 *
    newBusinessShare *
    neverRetry *
    (winRate / 100) *
    jobValue
  const lo = roundish(monthly * (assumed ? 0.5 : 0.75))
  const hi = roundish(monthly * (assumed ? 1.35 : 1.2))
  const annual = Math.round((((lo + hi) / 2) * 12) / 1000) * 1000
  return { lo, hi, annual }
}

/** Worksheet defaults derived from the answers. */
export function worksheetDefaults(answers: Answers) {
  // 75+ is open-ended, so its stand-in leans high — busy businesses tap it
  // and shouldn't have their leak underpriced.
  const callsMid =
    { u10: 6, "10-30": 20, "30-75": 45, "75+": 120, unknown: 30 }[
      answers.volume
    ] ?? 30
  const jobValue =
    { u250: 180, "250-750": 500, "750-2500": 850, "2500+": 3200 }[
      answers.value
    ] ?? 850
  // Missed share leans on the fallback answer: covered lines leak least,
  // voicemail and dead rings leak most.
  const missedShare = answers.coverage === "service" ? 0.12 : 0.27
  return {
    missedPerWeek: Math.round(callsMid * missedShare),
    winRate: 30,
    jobValue,
  }
}

/** The post-reveal gaps message: their own answers echoed back against the
 *  leak — diagnosis, not sell. Scripted from the answers alone until the
 *  generated variant lands (IMP-506): every sentence is something they told
 *  us, never something we claim to have observed. */
export function gapsMessage(answers: Answers) {
  const coverage = {
    voicemail:
      "You said calls roll to voicemail, and voicemail is where callers start dialing the next number on the list.",
    nothing:
      "You said unanswered calls just ring out, and a caller who gets no answer dials the next number on the list.",
    service:
      "You said an answering service picks up, which catches the call but still leaves the caller waiting on a callback.",
  }[answers.coverage]

  const followup = {
    day: "And your web leads hear back the same day, after many have already booked someone else.",
    later:
      "And your web leads hear back when someone gets to it, after many have already booked someone else.",
    none: "And your website doesn't bring you leads today, which usually means visitors have no quick way to start.",
  }[answers.followup]

  // No methodology closer ("that's where the number comes from") — the
  // scripted lines already carry their consequences, and a claim about how
  // the figure was computed is both the weakest sentence available and a
  // checkable one (IMP-506 follow-up).
  return [coverage, followup].filter(Boolean).join(" ")
}

/** IMP-507: how the reveal frames (or refuses to frame) the number.
 *
 *  Skip honesty first (round 3): the two dominant inputs are call volume and
 *  job value — with BOTH skipped there is nothing to compute FROM, so the
 *  reveal is suppressed entirely rather than a confident figure being built
 *  from stand-ins ("based on what you told me" cannot survive a run where
 *  nothing was told). With SOME core input skipped the range computes but
 *  widens, and the reveal names the assumption out loud.
 *
 *  Then the floor rule: a range that doesn't clear the recommended agent's
 *  own price argues against buying, so the reveal leads with per-job payback
 *  instead (DISCOVERY.md's small-number fallback) — but only when the job
 *  value actually came from the visitor; a payback pitch built on a stand-in
 *  value would be its own fabrication. */
export type RevealFraming =
  | { kind: "none" }
  | { kind: "missed"; lo: number; hi: number; annual: number; assumed: boolean }
  | { kind: "payback"; jobValue: number; months: number }

export function revealFraming(
  answers: Answers,
  recommendedPriceCents: number | null,
): RevealFraming {
  const missingVolume = !answers.volume
  const missingValue = !answers.value
  if (missingVolume && missingValue) return { kind: "none" }

  const assumed = missingVolume || missingValue || !answers.coverage
  const missed = missedRevenue(answers, assumed)
  const base: RevealFraming = { kind: "missed", ...missed, assumed }
  if (!recommendedPriceCents) return base

  const price = recommendedPriceCents / 100
  if (missed.lo >= price) return base
  // Payback needs a job value the visitor actually gave.
  if (missingValue) return base

  const { jobValue } = worksheetDefaults(answers)
  const months = Math.floor(jobValue / price)
  if (months < 2) return base

  return { kind: "payback", jobValue, months }
}

/** The website, as the conversation refers to it: bare host, no scheme or
 *  path. Until the lookup lands (IMP-503) this is also the only name we have
 *  for the business. */
export function siteLabel(website: string) {
  const trimmed = website.trim().toLowerCase()
  const withoutScheme = trimmed.replace(/^[a-z]+:\/\//, "")
  const host = withoutScheme.split(/[/?#]/)[0]
  return host.replace(/^www\./, "") || trimmed
}
